import { InlineAlertType } from '@varo/graphql-schema-types';
import { CheckImageType } from 'features/rdc/providers/RDCContextProvider/RDCContextProvider';
import { TFunction } from 'i18next';
import { ImageProps } from 'react-native';
import { SectionedContentScreenModel } from 'shared/containers/vxl/SectionedContentScreen/SectionedContentScreen';
import {
  VXLComponent,
  VXLComponentId,
  VXLGroup,
  VXLGroupId,
  VXLNode,
} from 'shared/renderer/types';
import theme from 'shared/styles/theme';

const formatScreenContent = ({
  amount,
  accountTitle,
  getImage,
  translatedText,
  editImageFront,
  editImageBack,
  depositFlowId,
  handleChangeAmountAction,
}: {
  amount?: string;
  accountTitle?: string;
  session?: string;
  editImageFront: () => void;
  editImageBack: () => void;
  getImage: (
    depositFlowId: string,
    key: CheckImageType,
  ) => ImageProps | null | undefined;
  translatedText: TFunction<'rdc', undefined>;
  handleChangeAmountAction: () => void;
  depositFlowId?: string;
}) => {
  const sectionsData: SectionedContentScreenModel = { sections: [] };
  const sectionHeader = (title: string): VXLNode => {
    return {
      id: 'review-deposit-page-section-header',
      name: VXLComponentId.SECTION_DIVIDER,
      props: {
        labelText: title,
      },
    };
  };

  const accountSectionItems: VXLComponent[] = [];
  if (amount) {
    accountSectionItems.push({
      id: 'review-deposit-amount-list-item',
      name: VXLComponentId.LIST_ITEM,
      props: {
        primaryCopy: '$' + amount,
        secondaryCopy: translatedText(
          'reviewDeposit.accountSection.amountListItemTitle',
        ),
        lineFormat: 'LABEL',
        showsChevron: true,
        analyticsId: 'checkDepositAmountEditClicked',
        onPress: handleChangeAmountAction,
      },
    });
  }

  if (accountTitle) {
    accountSectionItems.push({
      id: 'review-deposit-account-list-item',
      name: VXLComponentId.LIST_ITEM,
      props: {
        primaryCopy: accountTitle,
        secondaryCopy: translatedText(
          'reviewDeposit.accountSection.accountListItemTitle',
        ),
        lineFormat: 'LABEL',
        showsChevron: false,
      },
    });
  }

  sectionsData.sections.push({
    sectionHeader: sectionHeader(
      translatedText('reviewDeposit.accountSection.headerTitle'),
    ),
    data: accountSectionItems,
  });

  const checkImageSectionItems: VXLGroup[] = [];
  if (depositFlowId) {
    checkImageSectionItems.push({
      id: 'review-deposit-check-image-group',
      name: VXLGroupId.COMPONENT_GROUP,
      props: {
        alignItems: 'center',
        padding: theme.spacing.m,
        gap: theme.spacing.m,
        components: [
          {
            id: 'review-deposit-check-image-front',
            name: VXLComponentId.CHECK_IMAGE_PREVIEW,
            props: {
              imageProps: getImage(depositFlowId, 'front'),
              analyticsId: 'checkFrontImageRetakeClicked',
              onEditPress: editImageFront,
            },
          },
          {
            id: 'review-deposit-check-image-back',
            name: VXLComponentId.CHECK_IMAGE_PREVIEW,
            props: {
              imageProps: getImage(depositFlowId, 'back'),
              analyticsId: 'checkBackImageRetakeClicked',
              onEditPress: editImageBack,
            },
          },
        ],
      },
    });
  }

  sectionsData.sections.push({
    sectionHeader: sectionHeader(
      translatedText('reviewDeposit.checkImagesSection.headerTitle'),
    ),
    data: checkImageSectionItems,
  });

  const alertGroup: VXLGroup[] = [];
  alertGroup.push({
    id: 'review-deposit-informational-alert-group',
    name: VXLGroupId.COMPONENT_GROUP,
    props: {
      alignItems: 'center',
      padding: theme.spacing.m,
      gap: theme.spacing.m,
      components: [
        {
          id: 'review-deposit-informational-alert',
          name: VXLComponentId.INLINE_ALERT,
          props: {
            iconKeyType: 'INFO',
            bodyText: translatedText('reviewDeposit.footerSection.alert.body'),
            alertType: InlineAlertType.Info,
          },
        },
      ],
    },
  });

  sectionsData.sections.push({
    data: alertGroup,
  });

  return sectionsData;
};

export default formatScreenContent;
